import { Link } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { usePageMeta } from "@/hooks/usePageMeta";
import { ArrowRight, CheckCircle } from "lucide-react";

interface HowItWorksStep {
  title: string;
  description: string;
}

interface Benefit {
  title: string;
  description: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface RelatedFeature {
  name: string;
  href: string;
  description: string;
}

interface FeatureLandingPageProps {
  seoTitle: string;
  seoDescription: string;
  headline: string;
  subtitle: string;
  howItWorks: HowItWorksStep[];
  benefits: Benefit[];
  faqItems: FAQItem[];
  relatedFeatures: RelatedFeature[];
  structuredData: Record<string, unknown>[];
}

export default function FeatureLandingPage({
  seoTitle,
  seoDescription,
  headline,
  subtitle,
  howItWorks,
  benefits,
  faqItems,
  relatedFeatures,
  structuredData,
}: FeatureLandingPageProps) {
  usePageMeta(seoTitle, seoDescription);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navigation />

      <main className="flex-1">
        {/* Hero */}
        <section className="w-full py-20 px-6 md:px-10 bg-muted">
          <div className="container mx-auto max-w-[50rem] text-center">
            <h1 className="text-4xl md:text-6xl font-medium text-foreground leading-tight">
              {headline}
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-[40rem] mx-auto">
              {subtitle}
            </p>
            <div className="mt-8">
              <Link to="/auth/signup">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-3 gap-2 group shadow-lg hover:shadow-xl transition-all text-base">
                  <span>Start for Free</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="w-full py-20 px-6 md:px-10">
          <div className="container mx-auto max-w-[60rem]">
            <h2 className="text-3xl md:text-4xl font-medium text-foreground text-center mb-12">
              How It Works
            </h2>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {howItWorks.map((step, i) => (
                <div key={i} className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                      {i + 1}
                    </span>
                    <h3 className="text-lg font-semibold text-foreground">{step.title}</h3>
                  </div>
                  <p className="text-muted-foreground pl-11">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="w-full py-20 px-6 md:px-10 bg-muted">
          <div className="container mx-auto max-w-[60rem]">
            <h2 className="text-3xl md:text-4xl font-medium text-foreground text-center mb-12">
              Benefits for Students
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              {benefits.map((benefit, i) => (
                <div key={i} className="flex gap-4 p-6 rounded-xl bg-background border border-border">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Features */}
        <section className="w-full py-20 px-6 md:px-10">
          <div className="container mx-auto max-w-[60rem]">
            <h2 className="text-3xl md:text-4xl font-medium text-foreground text-center mb-12">
              Explore More Features
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              {relatedFeatures.map((feature) => (
                <Link
                  key={feature.href}
                  to={feature.href}
                  className="p-6 rounded-xl border border-border bg-background hover:border-primary/40 hover:shadow-md transition-all group"
                >
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    {feature.name}
                  </h3>
                  <p className="text-muted-foreground text-sm mt-1">{feature.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full py-20 px-6 md:px-10 bg-muted">
          <div className="container mx-auto max-w-[50rem]">
            <h2 className="text-3xl md:text-4xl font-medium text-foreground text-center mb-12">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-foreground">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full py-20 px-6 md:px-10">
          <div className="container mx-auto max-w-[40rem] text-center">
            <h2 className="text-3xl md:text-4xl font-medium text-foreground mb-4">
              Ready to Study Smarter?
            </h2>
            <p className="text-muted-foreground mb-8">
              Join thousands of students using Shattara AI to learn faster and ace their exams.
            </p>
            <Link to="/auth/signup">
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-3 gap-2 group shadow-lg hover:shadow-xl transition-all text-base">
                <span>Get Started Free</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* Structured Data */}
      {structuredData.map((data, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </div>
  );
}
